import type { IAdViewMeta } from "@finn-no/adview-provider-frontend-types";
import tracking from "@finn-no/analytics";
import type { EventTrackingConfig } from "@finn-no/analytics/lib/types.d.ts";
import pulse, { type TrackerData } from "@finn-no/pulse-sdk";
import {
  getSubverticalFromMobilityAdSchema,
  getTrackingCategoryForMobilityAdProviderAd,
} from "@m10s/mobility-mapping-utils";
import type { MobilityAd } from "@mobility-item-page/types";
import { EventType, getListingType } from "@mobility-item-page/utilities/client";
import { useCallback } from "react";
import { useAdViewAd, useAdViewMeta } from "src/context/AdViewProviderContext.tsx";

type Location = {
  latitude?: number;
  longitude?: number;
  accuracy?: number;
};

const trackPageView = (
  title: string,
  meta: IAdViewMeta,
  listingType: string,
  category: string,
  subVertical: string,
  location: Location | undefined,
): void => {
  // Tracking event: MO_pv03
  pulse.trackPageView({
    type: EventType.View,
    object: {
      type: EventType.ClassifiedAd,
      id: meta.adId.toString(),
      name: title,
      contentId: meta.adId.toString(),
      category,
      hasExtendedProfile: false,
      listingType,
      location,
    },
    vertical: {
      name: "mobility",
      subVertical,
    },
  });
};

const trackEvent = ({
  name,
  type,
  objectId,
  elementType,
  intent,
  target,
  objectExtras,
  position,
  title,
  meta,
  listingType,
  category,
  subVertical,
  location,
}: TrackerData): void => {
  pulse.trackEvent({
    type,
    intent: intent,
    name,
    object: {
      id: objectId,
      type: EventType.UIElement,
      elementType,
      position,
      location,
      ...objectExtras,
      items: [
        {
          type: EventType.ClassifiedAd,
          id: meta.adId.toString(),
          contentId: meta.adId.toString(),
          name: title,
          category,
          hasExtendedProfile: false,
          listingType,
        },
      ],
    },
    target,
    vertical: {
      name: "mobility",
      subVertical,
    },
  });
};

const trackEventCollectorPageView = (meta: IAdViewMeta) => {
  const config: EventTrackingConfig = {
    ecType: "AD",
    ecCategory: "NoOfPageViews_Main",
    eventData: {
      "ad.adtypeterm": meta.schemaName,
    },
    context: {
      finnkode: `${meta.adId}`,
    },
    analyticsName: "AD.PROSPECT_VIEW_CLICK",
  };

  if (meta.ownerId && config.eventData) {
    config.eventData["ad.ownerid"] = meta.ownerId.toString();
  }

  tracking.trackEvent(config);
};

const usePulse = () => {
  const ad = useAdViewAd();
  const meta = useAdViewMeta();
  const listingType = getListingType(meta.ownerUrn);
  const title = ad.title ?? "n/a";
  const subVertical = getSubverticalFromMobilityAdSchema(ad.adViewType);
  const category = getTrackingCategoryForMobilityAdProviderAd(ad);
  const location = getLocationTrackingObject(ad.location);
  const memoizedTrackEventCollectorPageView = useCallback(() => trackEventCollectorPageView(meta), [meta]);

  return {
    trackPageView: () => trackPageView(title, meta, listingType, category, subVertical, location),
    trackClick: ({ name, objectId, elementType, intent, target, objectExtras, position }: TrackerData) =>
      trackEvent({
        name,
        type: EventType.Click,
        objectId,
        elementType,
        intent,
        target,
        objectExtras,
        position,
        location,
        title,
        meta,
        listingType,
        category,
        subVertical,
      }),
    trackElementView: ({ name, objectId, elementType, intent, target, objectExtras, position }: TrackerData) =>
      trackEvent({
        name,
        type: EventType.View,
        objectId,
        elementType,
        intent,
        target,
        objectExtras,
        position,
        location,
        title,
        meta,
        listingType,
        category,
        subVertical,
      }),
    trackEventCollectorPageView: memoizedTrackEventCollectorPageView,
  };
};

export default usePulse;

const getLocationTrackingObject = (location: MobilityAd["location"]): Location | undefined => {
  if (!location?.position || !location?.position.lat || !location?.position.lng || !location?.position.accuracy) {
    return undefined;
  }
  return {
    latitude: location?.position?.lat,
    longitude: location?.position?.lng,
    accuracy: location?.position?.accuracy,
  };
};
